import { IJobDetailsForm } from '../Types';
import { IDraftJobPostEntity, ISaveDraftJobPostPayload } from '~/domain/PermanentJobs/types';
import { PayType, CompensationType } from '~/domain/PermanentJobs/enums';
import { EnumHelper } from '~/utils/EnumHelper';
import { UserSubProfession } from '~/enums/UserProfession';
import { EducationUtils } from '~/utils/EducationUtils';

/**
 * Mapper class for JobDetailsForm data transformations
 * Handles conversions from domain models to form data
 *
 * @example
 * // Convert draft domain data to form data for editing
 * const formData = JobDetailsFormMapper.fromDraft(draftJobPost);
 */
export class JobDetailsFormMapper {
  /**
   * Converts draft domain data to form data
   * Always returns complete form data - draft parameter is optional
   *
   * @param draft - Optional domain draft job post data
   * @returns Complete form data with draft values merged with defaults
   */
  static fromDraft(draft?: IDraftJobPostEntity): IJobDetailsForm {
    // Business logic: subProfession takes precedence over professionType
    const professionType = draft?.subProfession || draft?.professionType || '';

    // Convert numeric rates to strings for form compatibility
    const minRate = draft?.minRate !== undefined ? String(draft.minRate) : '';
    const maxRate = draft?.maxRate !== undefined ? String(draft.maxRate) : '';

    // Infer compensation type from rates
    // FIXED: minRate == maxRate or only minRate exists
    // RANGE: minRate != maxRate
    const compensationType = this.inferCompensationType(minRate, maxRate);

    return {
      office: draft?.officeId ?? null,
      jobTitle: draft?.title ?? '',
      professionType,
      officeSpecialty: draft?.officeSpecialty ? `specialty_${draft.officeSpecialty}` : '',
      payType: draft?.payType ?? PayType.SALARY,
      compensationType,
      minRate,
      maxRate,
      benefits: draft?.benefits ?? [],
      benefitsOther: draft?.benefitsOther ?? '',
      scheduleType: draft?.scheduleType ?? '',
      expectedDays: draft?.expectedDays ?? [],
      jobDescription: draft?.description ?? '',
    };
  }

  /**
   * Infers compensation type from min and max rates
   * - FIXED: when minRate == maxRate or only minRate exists
   * - RANGE: when minRate != maxRate
   * - Default: FIXED when no rates provided
   */
  private static inferCompensationType(minRate: string, maxRate: string): CompensationType {
    // No rates provided - default to FIXED
    if (!minRate && !maxRate) {
      return CompensationType.FIXED;
    }

    // Only minRate provided - FIXED
    if (minRate && !maxRate) {
      return CompensationType.FIXED;
    }

    // Both rates provided - compare values
    if (minRate && maxRate) {
      const min = parseFloat(minRate.replace(/,/g, ''));
      const max = parseFloat(maxRate.replace(/,/g, ''));

      // Same values - FIXED
      if (min === max) {
        return CompensationType.FIXED;
      }

      // Different values - RANGE
      return CompensationType.RANGE;
    }

    // Default fallback
    return CompensationType.FIXED;
  }

  /**
   * Converts form data to save draft job post payload
   * Transforms form values to API-compatible format
   *
   * @param formData - Form data from JobDetailsForm
   * @returns Payload ready for save draft API call
   */
  static toSaveDraftPayload(formData: IJobDetailsForm): ISaveDraftJobPostPayload {
    // Helper to parse string rates to numbers
    const parseRate = (rate: string): number | undefined => {
      if (!rate) return undefined;
      const parsed = parseFloat(rate.replace(/,/g, ''));
      return isNaN(parsed) ? undefined : parsed;
    };

    const profession = formData.professionType;
    const isSubProfession = EnumHelper.getObjectKeys(UserSubProfession).includes(profession);
    const professionType = isSubProfession
      ? UserSubProfession.getProfession(profession)
      : formData.professionType;
    const subProfession = isSubProfession ? profession : undefined;

    // Remove "specialty_" prefix from officeSpecialty if present
    const officeSpecialty = EducationUtils.removeSpecialtyPrefix(formData.officeSpecialty);

    const minRateNum = parseRate(formData.minRate);
    const maxRateNum = parseRate(formData.maxRate);

    // Determine rate fields based on compensationType
    let minRate: number | undefined;
    let maxRate: number | undefined;

    if (formData.compensationType === CompensationType.FIXED) {
      // FIXED compensation: use minRate and maxRate (same value)
      minRate = minRateNum;
      maxRate = minRateNum;
    } else if (formData.compensationType === CompensationType.RANGE) {
      // RANGE compensation: use minRate and maxRate
      minRate = minRateNum;
      maxRate = maxRateNum;
    }

    return {
      officeId: formData.office || null,
      title: formData.jobTitle || null,
      professionType: professionType || null,
      subProfession: subProfession || null,
      officeSpecialty: officeSpecialty || null,
      payType: formData.payType || null,
      minRate: minRate ?? null,
      maxRate: maxRate ?? null,
      benefits: formData.benefits?.length ? formData.benefits : null,
      benefitsOther: formData.benefitsOther || null,
      scheduleType: formData.scheduleType || null,
      expectedDays: formData.expectedDays?.length ? formData.expectedDays : null,
      description: formData.jobDescription || null,
    };
  }
}
